import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./home.css";
import { FaCalendarAlt, FaUser, FaSearch, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import ComfortableVilla from "../comfortableVilla/comfortableVilla";
import Vacation from "../vacation/vacation";
import PerfectVilla from "../perfectVilla/perfectVilla";
import { DatePicker } from "antd";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const Home = () => {
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guests, setGuests] = useState("");
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleSearch = () => {
    const queryParams = new URLSearchParams();
    if (checkInDate) {
      queryParams.append("checkIn", checkInDate.format("DD-MM-YYYY"));
    }
    if (checkOutDate) {
      queryParams.append("checkOut", checkOutDate.format("DD-MM-YYYY"));
    }
    if (guests) {
      queryParams.append("guests", guests);
    }
    navigate(`/booking?${queryParams.toString()}`);
  };

  return (
    <>
      <div className="relative w-full md:min-h-screen h-[75%] ">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover z-0"
          autoPlay
          loop
          muted // Start muted
          poster="/videos/poster.png"
        >
          <source src="/videos/gir-glades-1080p.mp4" type="video/mp4" />
          <source src="/videos/gir-glades-1080p.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>

        {/* Mute/Unmute Button (Mobile) */}
        <button
          onClick={toggleMute}
          className="absolute top-10 left-1/2 -translate-x-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-50 md:hidden"
>
          {isMuted ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
        </button>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 z-0"></div>

        {/* Content Wrapper */}
        <div className="relative z-10 flex flex-col items-center justify-center md:h-screen h-[700px] text-center text-white px-4">
          <div className="text-4xl md:text-6xl font-normal tracking-wide md:tracking-wider leading-tight md:leading-snug">
            WELCOME TO <br />
            <span className="text-[#D1A752]">GIR GLADES</span>
          </div>
        </div>

        <div className="absolute md:bottom-10 bottom-[-80px] left-1/2 transform -translate-x-1/2 md:flex items-center bg-white/10 backdrop-blur-lg md:h-[100px] border border-white/20 px-4 w-[320px] rounded-[40px] md:rounded-full md:w-[1005px] z-20">
          <div className="flex items-center bg-transparent border max-md:mt-8 max-md:mb-4 border-white/30 px-4 py-3 rounded-[26px] md:rounded-full md:w-1/3 md:mx-2 text-white relative z-30 cursor-pointer">
            <DatePicker
              value={checkInDate ? dayjs(checkInDate, 'DD-MM-YYYY') : null}
              onChange={(date, dateString) => setCheckInDate(dateString)}
              format="DD-MM-YYYY"
              placeholder="Check In"
              className="bg-transparent hover:bg-transparent focus:bg-transparent focus:outline-none border-none w-full text-white placeholder-white/70"
              suffixIcon={null}
              popupClassName="bg-white text-black p-2 rounded-lg"
            />
            <FaCalendarAlt className="mr-2 text-gray-300" />
          </div>
          {/* Check-out Input */}
          <div className="flex items-center bg-transparent border max-md:mt-4 max-md:mb-4 border-white/30 px-4 py-3 rounded-[26px] md:rounded-full md:w-1/3 md:mx-2 text-white relative z-30 cursor-pointer">
            <DatePicker
              value={checkOutDate ? dayjs(checkOutDate, 'DD-MM-YYYY') : null}
              onChange={(date, dateString) => setCheckOutDate(dateString)} 
              format="DD-MM-YYYY"
              placeholder="Check Out"
              className="bg-transparent hover:bg-transparent focus:bg-transparent focus:outline-none border-none w-full text-white placeholder-gray-300"
              suffixIcon={null}
              popupClassName="bg-white text-black p-2 rounded-lg"
            />
            <FaCalendarAlt className="mr-2 text-gray-300" />
          </div>

          {/* Guest Input */}
          <div className="flex items-center bg-transparent border max-md:mt-4 max-md:mb-8 border-white/30 px-4 py-3 rounded-[26px] md:rounded-full md:w-1/3 text-white relative z-30">
            <input
              type="number"
              value={guests}
              onChange={(e) => setGuests(Math.max(1, Number(e.target.value)))}
              min={1}
              className="bg-transparent focus:outline-none w-full text-white placeholder-gray-300 cursor-pointer"
              placeholder="Number of Guests"
            />
            <FaUser className="mr-2 text-gray-300" />
          </div>
          <div
            className="items-center bg-[#D1A752] text-white  flex justify-center  max-md:mt-4 max-md:mb-8  px-4 py-3 rounded-[26px] md:hidden relative z-30"
            onClick={handleSearch}
          >
            Search
          </div>

          {/* Search Button */}
          <button
            className="ml-4 max-md:hidden bg-[#D1A752] p-4 rounded-full hover:bg-[#D1A752] transition duration-300 relative z-30"
            onClick={handleSearch}
          >
            <FaSearch className="text-white text-lg" />
          </button>
          {/* Mute/Unmute Button (Desktop) */}
          <button
            onClick={toggleMute}
            className="ml-4 max-md:hidden bg-black bg-opacity-50 text-white p-4 rounded-full z-30"
          >
            {isMuted ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
          </button>
        </div>
      </div>
      <ComfortableVilla />
      <Vacation />
      <PerfectVilla />
    </>
  );
};

export default Home;
